import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import Sidebar from "../../components/layout/Sidebar";
import Header from "../../components/layout/Header";
import {
  Search,
  Filter,
  PlusSquare,
  Edit,
  Trash2,
  StickyNoteIcon,
  Link as LinkIcon,
  Layers,
  XCircle,
  CheckCircle,
  RotateCwIcon,
} from "lucide-react";
import Pagination from "../../components/Pagination";
import { useTrigger } from "../../hooks/useTrigger";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import PageForm from "./PageForm";
import { toast } from "react-toastify";
import { useResolvedPath } from "react-router-dom";
import Skeleton from "../../components/ui/Skeleton";
import DeleteModal from "../../components/ui/DeleteModal";
import { useFileUpload } from "../../hooks/useFileUplode";

function Trigger() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPage, setSelectedPage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // view, edit, create
  const [contentsList, setContentsList] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState({
    trigger_type: "ADMIN_ADDED",
  });
  const [pageData, setPageData] = useState({
    currentPage: 1,
    totalPages: 1,
    showing: 10,
    total: 0,
  });

  const queryClient = useQueryClient();

  const { fetchTrigger, createTrigger, updateTrigger, deleteTrigger } =
    useTrigger({
      filters: statusFilter,
      pagination: { page: pageData.currentPage, limit: 10 },
      sort: { field: "created_at", direction: "desc" },
    });
  const { fileUpload } = useFileUpload();

  const contentsQuery = useQuery({
    queryKey: ["Trigger", pageData.currentPage, statusFilter],
    queryFn: () =>
      fetchTrigger({
        pagination: { page: pageData.currentPage, limit: pageData.showing },
      }),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (!contentsQuery.isLoading && contentsQuery.data) {
      const response = contentsQuery.data;
      setContentsList(response.data.trigger || []);
      setPageData({
        currentPage: response.data.pagination?.currentPage || 1,
        totalPages: response.data.pagination?.lastPage || 1,
        total: response.data.pagination?.total || 0,
        showing: response.data.pagination?.showing || 10,
      });
    }
  }, [contentsQuery.data, contentsQuery.isLoading]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const openModal = (trigger, mode) => {
    setSelectedPage(trigger);
    setModalMode(mode);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPage(null);
  };

  const handlePageChange = (page) => {
    setPageData((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };

  const filteredPages = contentsList.filter((trigger) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        (trigger.trigger_text &&
          trigger.trigger_text.some((t) =>
            t.text.toLowerCase().includes(query)
          )) ||
        (trigger.user_id && trigger.user_id.toLowerCase().includes(query))
      );
    }
    return true;
  });

  const handleUpdate = (page) => {
    openModal(page, "edit");
  };

  const handleDelete = {
    onDelete: (page) =>
      mutation.deleteTrigger.mutate(page._id, {
        onSuccess: (data) => {
          toast.success(data.message || "Trigger Deleted");
          setDeleteModal(false);
        },
        onError: (error) => {
          toast.error(error.message || "Failed to delete page");
        },
      }),
    onClose: () => setDeleteModal(false),
    onOpen: (page) => {
      setDeleteModal(true);
      setSelectedPage(page);
    },
  };

  const toggleStatus = (page) => {
    mutation.toggleStatus.mutate(page._id, {
      onSuccess: (data) => {
        toast.success("Status updated successfully");
      },
      onError: (error) => toast.error("Failed to update status"),
    });
  };

  const mutation = {
    createTrigger: useMutation({
      mutationFn: (body) => {
        if (modalMode === "edit") {
          return updateTrigger(body);
        }
        return updateTrigger(body);
      },
    }),
    deleteTrigger: useMutation({
      mutationFn: (id) => deleteTrigger(id),
    }),
    // toggleStatus: useMutation({
    //   mutationFn: async (id) => await toggleTriggerStatus(id),
    // }),
  };

  const handleSubmit = (body) => {
    if (modalMode === "edit") {
      body._id = selectedPage._id;
    }

    mutation.createTrigger.mutate(body, {
      onSuccess: async (data) => {
        await queryClient.invalidateQueries(["Trigger"]);
        toast.success(
          data?.data?.message ||
            `Page ${modalMode === "edit" ? "Updated" : "Created"} successfully`
        );
        setShowModal(false);
      },
      onError: (error) => {
        toast.error(error.message || "Failed to save page");
      },
    });
  };

  return (
    <div>
      <main className="">
        {/* Header and Add Button */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold">Trigger</h1>
          {statusFilter.trigger_type === "ADMIN_ADDED" && (
            <button
              onClick={() => {
                filteredPages.length > 0
                  ? handleUpdate(filteredPages[0])
                  : openModal({}, "create");
              }}
              className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
            >
              <PlusSquare className="mr-2 h-4 w-4" />
              Add Trigger
            </button>
          )}
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search sounds..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={statusFilter.trigger_type}
                onChange={(e) =>
                  setStatusFilter((prev) => ({
                    ...prev,
                    trigger_type: e.target.value,
                  }))
                }
                className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="ALL">All</option>
                <option value="USER_ADDED">User Added Sounds</option>
                <option value="ADMIN_ADDED">Admin Added Sounds</option>
              </select>
              <button
                className="flex items-center justify-center bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter((prev) => ({
                    ...prev,
                    sound_type: "ALL",
                  }));
                  setPageData((prev) => ({
                    ...prev,
                    currentPage: 1,
                  }));
                }}
              >
                <RotateCwIcon className="h-4 w-4" />
                <span className="ml-2">Reset</span>
              </button>
            </div>
          </div>
          {/* Table or Skeleton */}
          {contentsQuery.isLoading ? (
            <Skeleton.Card header rows={10} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Added By
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Trigger Texts
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Created At
                    </th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPages.length > 0 ? (
                    filteredPages.map((trigger) => (
                      <tr
                        key={trigger._id}
                        className="hover:bg-gray-50 border-b"
                      >
                        {/* Added By */}
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <div className="font-medium text-gray-900">
                            {trigger.full_name || "N/A"}
                          </div>
                          <div className="text-xs text-gray-500">
                            {trigger.phone || "N/A"}
                          </div>
                        </td>

                        {/* Trigger Texts */}
                        <td className="px-4 py-3">
                          <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                            {trigger.trigger_text &&
                            trigger.trigger_text.length > 0 ? (
                              trigger.trigger_text.map((item, idx) => (
                                <li key={idx}>{item?.text}</li>
                              ))
                            ) : (
                              <span className="text-gray-400">
                                No trigger texts
                              </span>
                            )}
                          </ul>
                        </td>
                        <td>
                          <div className="text-xs text-gray-400">
                            {moment(trigger.created_at).format(
                              "MMMM Do YYYY, h:mm a"
                            )}
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => openModal(trigger, "view")}
                              className="text-gray-400 hover:text-gray-500"
                              title="View Details"
                            >
                              <StickyNoteIcon className="h-4 w-4" />
                            </button>
                            {/* <button
                              onClick={() => handleUpdate(trigger)}
                              className="text-blue-400 hover:text-blue-500"
                              title="Edit Trigger"
                            >
                              <Edit className="h-4 w-4" />
                            </button> */}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No triggers found matching your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            currentPage={pageData.currentPage}
            onPageChange={handlePageChange}
            showing={pageData.showing}
            totalPages={pageData.totalPages}
            total={pageData.total}
          />
        </div>
      </main>
      <DeleteModal
        isOpen={deleteModal}
        onClose={handleDelete.onClose}
        onDelete={() => handleDelete.onDelete(selectedPage)}
        title="Page"
      />
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={closeModal}
          ></div>
          <div className="h-4/5 relative bg-white rounded-2xl max-w-2xl w-full mx-4 shadow-xl overflow-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium">
                {modalMode === "view"
                  ? "Trigger Details"
                  : modalMode === "edit"
                  ? "Edit Trigger"
                  : "Create Trigger"}
              </h3>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
                onClick={closeModal}
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <PageForm
                mode={modalMode}
                defaultValues={
                  selectedPage
                    ? {
                        ...selectedPage,
                        created_at: moment(selectedPage.created_at).format(
                          "YYYY-MM-DD"
                        ),
                        trigger_text: selectedPage.trigger_text || [],
                      }
                    : {}
                }
                onCancel={closeModal}
                onSubmit={
                  modalMode === "view" ? () => {} : (data) => handleSubmit(data)
                }
                submissionLoading={
                  modalMode === "view"
                    ? false
                    : mutation.createTrigger.isLoading
                }
                fileUpload={fileUpload}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Trigger;
